// PWA update prompt (best-practice "prompt" pattern).
//
// Owns service-worker registration and update detection. When a new version
// finishes installing while the app is already controlled by an older worker,
// it surfaces a small, dismissible banner. The user taps "Update" → we post
// SKIP_WAITING to the waiting worker → it activates → `controllerchange` fires
// → we reload once into the latest build. First-time installs stay silent.

import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

const T = {
  teal: '#14B8A6',
  text: '#fff',
  text50: 'rgba(255,255,255,.5)',
  mono: "'JetBrains Mono', monospace",
  serif: "'Instrument Serif', serif",
  sans: "'Outfit', sans-serif",
};

export default function PWAUpdatePrompt() {
  const [show, setShow] = useState(false);
  const waitingRef = useRef<ServiceWorker | null>(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Reload once the user-accepted update takes control.
    const onControllerChange = () => {
      if (!reloadingRef.current) return;
      reloadingRef.current = false;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Surface a waiting worker — but only as an *update* (skip first install).
    const offerUpdate = (worker: ServiceWorker | null) => {
      if (!worker || !navigator.serviceWorker.controller) return;
      waitingRef.current = worker;
      setShow(true);
    };

    let intervalId: number | undefined;
    const regRef: { current: ServiceWorkerRegistration | null } = { current: null };
    const onVisible = () => {
      if (document.visibilityState === 'visible') regRef.current?.update().catch(() => {});
    };

    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then(registration => {
        regRef.current = registration;
        console.log('✅ SW registered:', registration.scope);

        // A worker may already be waiting from a previous session.
        if (registration.waiting) offerUpdate(registration.waiting);

        // Detect a new worker installing.
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed') offerUpdate(registration.waiting ?? installing);
          });
        });

        // Check for updates on launch, hourly, and whenever the app is refocused
        // (the common path for an installed home-screen PWA).
        registration.update().catch(() => {});
        intervalId = window.setInterval(() => registration.update().catch(() => {}), 60 * 60 * 1000);
        document.addEventListener('visibilitychange', onVisible);
      })
      .catch(error => console.error('❌ SW registration failed:', error));

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisible);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const handleUpdate = () => {
    const worker = waitingRef.current;
    setShow(false);
    if (!worker) { window.location.reload(); return; }
    reloadingRef.current = true;
    worker.postMessage({ type: 'SKIP_WAITING' });
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed', left: '50%', transform: 'translateX(-50%)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
        zIndex: 60, width: 'calc(100% - 32px)', maxWidth: 400,
        display: 'flex', alignItems: 'center', gap: 12,
        background: '#141414', border: '1px solid rgba(255,255,255,.1)',
        borderRadius: 14, padding: '13px 14px 13px 16px',
        boxShadow: '0 16px 44px rgba(0,0,0,.6)',
        animation: 'pwaSlideUp .3s ease',
      }}
      role="status"
    >
      <style>{`@keyframes pwaSlideUp{from{opacity:0;transform:translate(-50%,12px)}to{opacity:1;transform:translate(-50%,0)}}`}</style>

      <div style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: 'rgba(20,184,166,.12)', border: '1px solid rgba(20,184,166,.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.teal,
      }}>
        <RefreshCw size={16} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.serif, fontSize: 16, color: T.text, lineHeight: 1.1 }}>Update available</div>
        <div style={{ fontFamily: T.sans, fontSize: 11.5, color: T.text50, marginTop: 2 }}>A new version is ready to install.</div>
      </div>

      <button
        onClick={handleUpdate}
        style={{
          flexShrink: 0, padding: '9px 14px', borderRadius: 9,
          background: T.teal, color: '#06231f', border: 'none', cursor: 'pointer',
          fontFamily: T.mono, fontSize: 10, letterSpacing: '.08em', fontWeight: 500,
        }}
      >
        UPDATE
      </button>

      <button
        onClick={() => setShow(false)}
        aria-label="Dismiss"
        style={{
          flexShrink: 0, width: 30, height: 30, borderRadius: 8,
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: T.text50, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <X size={15} />
      </button>
    </div>
  );
}
